import { getDayJsFormatFromLocale } from '@repo/utils'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { Maybe } from 'graphql/jsutils/Maybe'
import React from 'react'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import { useLocale } from '../../../../../hooks/use-locale'
import { useDateFormatter } from '../../../../../lib/hooks'
import { IconSvg } from '../../../../atoms'

/**
 * Mandatory for dayJs to format time
 */
dayjs.extend(customParseFormat)

export interface ProposalDatesProps {
  startDate: Date
  endDate: Date
  hours?: Maybe<{
    arrivalHour?: Maybe<string>
    departureHour?: Maybe<string>
  }>
}
const ProposalDates = ({ endDate, startDate, hours }: ProposalDatesProps) => {
  const dateFormatter = useDateFormatter()

  const computedTime = computeArrivalDepartureTime({ hours })

  return (
    <div className={s.dates}>
      <IconSvg.Calendar
        style={{ width: 13, height: 13, paddingTop: 3, marginRight: 4 }}
      />

      <span>
        {dateFormatter
          .format({
            date: startDate,
            useUTC: true,
            formatString: 'dayMonthYear',
          })
          .replace('.', '')}
      </span>

      {startDate !== endDate ? (
        <>
          <span> - </span>
          <span>
            {dateFormatter
              .format({
                date: endDate,
                useUTC: true,
                formatString: 'dayMonthYear',
              })
              .replace('.', '')}
          </span>
        </>
      ) : null}

      {computedTime ? (
        <>
          (&nbsp;
          <FormattedMessage
            id='ui.ProposalDates.hours'
            defaultMessage='de {arrivalHour} à {departureHour}'
            values={{ ...computedTime }}
          />
          &nbsp; )
        </>
      ) : null}
    </div>
  )
}

export default ProposalDates

const computeArrivalDepartureTime = ({
  hours,
}: Pick<ProposalDatesProps, 'hours'>): null | {
  arrivalHour: string
  departureHour: string
} => {
  if (!hours) {
    return null
  }

  const { locale } = useLocale()

  const dayJsLocale = getDayJsFormatFromLocale(locale)

  const arrivalHour = dayjs(hours.arrivalHour, 'HH:mm')
    .locale(dayJsLocale.locale)
    .format(dayJsLocale.format)
  const departureHour = dayjs(hours.departureHour, 'HH:mm')
    .locale(dayJsLocale.locale)
    .format(dayJsLocale.format)

  return { arrivalHour, departureHour }
}
